import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { Users, Trophy, Calendar, Shield, Plus, Send, TrendingUp, X, Save, Trash2, Lock, Key, Building, Mail, Upload, UserCheck, UserX, RefreshCw, Download, AlertCircle, CheckCircle, ChevronDown, ChevronUp, BarChart2, Eye, EyeOff,Palette  } from 'lucide-react';
import { apiFetch, API_BASE_URL } from "./lib/api.js";

// ===================================================================================
// 2. HELPERS
// ===================================================================================
const escapeHTML = (str) => {
  if (!str || typeof str !== 'string') return "";
  return str.replaceAll(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[m]);
};

const renderRankingIcons = (triangles = 0, stars = 0) => {
  const icons = [];
  for (let i = 0; i < triangles; i++) icons.push(<span key={`tri-${i}`} style={{ fontSize: '1.2em', marginRight: '4px' }} title="Nivel Completado">🔺</span>);
  for (let i = 0; i < stars; i++) icons.push(<span key={`star-${i}`} className="text-yellow-400 text-xl">★</span>);
  if (triangles === 0) {
    for (let i = 0; i < Math.max(0, 5 - stars); i++) icons.push(<span key={`empty-${i}`} className="text-gray-200 text-xl">★</span>);
  }
  return <div className="flex items-center">{icons}</div>;
};


// --- Helper 1: cuenta usuarios únicos que respondieron una semana concreta ---
function countUniqueUsersThisWeek(responses, week) {
  const ids = responses
    .filter(r => r.week === week)
    .map(r => String(r.userId?._id ?? r.userId))
    .filter(uid => uid && uid !== 'undefined' && uid !== 'null');
  return new Set(ids).size;
}

// --- Helper 2: calcula las stats de un departamento dado ---
async function computeDeptStats(d, currentWeek) {
  const { count: totalUsers } = await apiFetch(`/api/users/count/${d.name}`);
  const usersThisWeek = countUniqueUsersThisWeek(d.responses, currentWeek);
  const participationRate = totalUsers > 0
    ? Math.min(Math.round((usersThisWeek / totalUsers) * 100), 100)
    : 0;

  return {
    name: d.name,
    avgCompromiso: d.count > 0 ? Number.parseFloat((d.totalC / d.count).toFixed(1)) : 0,
    avgSatisfaccion: d.count > 0 ? Number.parseFloat((d.totalS / d.count).toFixed(1)) : 0,
    responseCount: d.count,
    totalUsers,
    participationRate,
    usersThisWeek,
  };
}

// ===================================================================================
// TEMA / PERSONALIZACIÓN
// ===================================================================================
const DEFAULT_THEME = {
  appName: 'Trivial Laboral',
  primaryColor: '#2563eb',
  secondaryColor: '#9333ea',
  backgroundColor: '#f9fafb',
  fontFamily: 'system',
  borderRadius: 'md',
  logoData: null,
};

const FONT_STACKS = {
  system: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  serif: 'Georgia, Cambria, "Times New Roman", serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  rounded: '"Trebuchet MS", "Segoe UI", system-ui, sans-serif',
};
const FONT_LABELS = { system: 'Sistema (por defecto)', serif: 'Serif', mono: 'Monoespaciada', rounded: 'Redondeada' };

// valores para rounded-lg / rounded-xl / rounded-2xl
const RADIUS = {
  none: ['0px', '0px', '0px'],
  sm: ['0.25rem', '0.375rem', '0.5rem'],
  md: ['0.5rem', '0.75rem', '1rem'],
  lg: ['0.75rem', '1rem', '1.5rem'],
};
const RADIUS_LABELS = { none: 'Sin redondeo', sm: 'Suave', md: 'Normal (por defecto)', lg: 'Muy redondeado' };

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const colorFamilyCSS = (name, v) => {
  const dark = `color-mix(in srgb, var(${v}) 82%, black)`;
  const tint = (p) => `color-mix(in srgb, var(${v}) ${p}%, white)`;
  return `
.bg-${name}-500,.bg-${name}-600{background-color:var(${v})!important}
.bg-${name}-700,.hover\\:bg-${name}-700:hover{background-color:${dark}!important}
.bg-${name}-50{background-color:${tint(8)}!important}
.bg-${name}-100,.hover\\:bg-${name}-50:hover{background-color:${tint(16)}!important}
.text-${name}-500,.text-${name}-600,.hover\\:text-${name}-500:hover,.hover\\:text-${name}-800:hover{color:var(${v})!important}
.text-${name}-700,.text-${name}-800{color:${dark}!important}
.border-${name}-200,.border-${name}-300,.border-${name}-400,.hover\\:border-${name}-400:hover{border-color:${tint(35)}!important}
.focus\\:ring-${name}-500:focus{--tw-ring-color:var(${v})!important}
.peer:checked ~ .peer-checked\\:bg-${name}-600{background-color:var(${v})!important}
`;
};

const buildThemeCSS = (t) => {
  const [a, b, c] = RADIUS[t.borderRadius] || RADIUS.md;
  return `
:root{--brand-primary:${t.primaryColor};--brand-secondary:${t.secondaryColor};--brand-bg:${t.backgroundColor}}
body{background-color:var(--brand-bg);font-family:${FONT_STACKS[t.fontFamily] || FONT_STACKS.system}}
.bg-gray-50.min-h-screen{background-color:var(--brand-bg)!important}
.rounded-lg{border-radius:${a}!important}
.rounded-xl{border-radius:${b}!important}
.rounded-2xl{border-radius:${c}!important}
${colorFamilyCSS('blue', '--brand-primary')}
${colorFamilyCSS('purple', '--brand-secondary')}
`;
};

const ThemeContext = createContext({ theme: DEFAULT_THEME, setTheme: () => {}, reloadTheme: async () => {} });
const useTheme = () => useContext(ThemeContext);

const ThemeStyles = ({ theme }) => <style>{buildThemeCSS(theme)}</style>;

const BrandLogo = ({ size = 'md' }) => {
  const { theme } = useTheme();
  if (!theme.logoData?.startsWith('data:image/')) return null;
  return (
    <img
      src={theme.logoData}
      alt={`Logo de ${theme.appName}`}
      className="object-contain"
      style={{ maxHeight: size === 'lg' ? 72 : 44, maxWidth: size === 'lg' ? 220 : 160 }}
    />
  );
};

// ===================================================================================
// 3. COMPONENTE: IMPORTACIÓN DE USUARIOS VÍA EXCEL
// ===================================================================================
const UserImportPanel = ({ onImportDone }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); setResult(null); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && (f.name.endsWith('.xlsx') || f.name.endsWith('.xls'))) {
      setFile(f); setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const data = await apiFetch('/api/admin/import-users', {
        method: 'POST',
        body: formData,
      });
      setResult(data);
      if (data.summary.created > 0) onImportDone();
    } catch (err) {
      setResult({ error: err.message });
    }
    setImporting(false);
  };

  const handleSendCodes = async (ids) => {
    setSending(true);
    try {
      const data = await apiFetch('/api/admin/users/send-codes', {
        method: 'POST',
        body: { userIds: ids },
      });
      alert(`✅ Enviados: ${data.summary.sent} | Fallidos: ${data.summary.failed}`);
    } catch (err) { alert(err.message || 'Error de conexión'); }
    setSending(false);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const allIds = result?.details?.created?.map(u => u._id) || [];
    setSelectedIds(selectedIds.length === allIds.length ? [] : allIds);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <Upload className="w-6 h-6 text-blue-600" />
          Importar Usuarios desde Excel
        </h3>
        <a href="/plantilla_usuarios.xlsx" download className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded-lg px-3 py-1.5 hover:bg-blue-50 transition">
          <Download className="w-4 h-4" /> Descargar Plantilla
        </a>
      </div>
      <div
  onDrop={handleDrop}
  onDragOver={(e) => e.preventDefault()}
  onClick={() => fileInputRef.current?.click()}
  onKeyDown={(e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  }}
  role="button"
  tabIndex={0}
  aria-label="Seleccionar o arrastrar archivo Excel"
  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition mb-4 ${file ? 'border-green-400 bg-green-50' : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'}`}
>
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileChange} className="hidden" />
        {file ? (
          <div>
            <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
            <p className="font-medium text-green-700">{file.name}</p>
            <p className="text-sm text-green-600">{(file.size / 1024).toFixed(1)} KB — Listo para importar</p>
          </div>
        ) : (
          <div>
            <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
            <p className="font-medium text-gray-600">Arrastra tu archivo Excel aquí</p>
            <p className="text-sm text-gray-400">o haz clic para seleccionarlo (.xlsx, .xls)</p>
            <p className="text-xs text-gray-400 mt-2">Columnas: Nombre · Apellido · Departamento · Email</p>
          </div>
        )}
      </div>
      <button onClick={handleImport} disabled={!file || importing} className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2">
        {importing ? <><RefreshCw className="w-5 h-5 animate-spin" /> Importando...</> : <><Upload className="w-5 h-5" /> Importar Usuarios</>}
      </button>
      {result && !result.error && (
        <div className="mt-4">
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{result.summary.created}</p>
              <p className="text-xs text-green-700">Creados</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-yellow-600">{result.summary.skipped}</p>
              <p className="text-xs text-yellow-700">Omitidos</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-red-600">{result.summary.errors}</p>
              <p className="text-xs text-red-700">Errores</p>
            </div>
          </div>
          {result.details?.created?.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-700 text-sm">Usuarios creados — selecciona a quiénes enviar el código</h4>
                <div className="flex gap-2">
                  <button onClick={() => handleSendCodes(selectedIds)} disabled={selectedIds.length === 0 || sending} className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {sending ? 'Enviando...' : `Enviar a seleccionados (${selectedIds.length})`}
                  </button>
                  <button onClick={() => handleSendCodes([])} disabled={sending} className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1">
                    <Send className="w-3 h-3" /> Enviar a todos
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full text-sm divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2"><input type="checkbox" checked={selectedIds.length === result.details.created.length} onChange={toggleSelectAll} className="rounded" /></th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Departamento</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {result.details.created.map((u) => (
                      <tr key={u._id} className={`hover:bg-gray-50 ${selectedIds.includes(u._id) ? 'bg-blue-50' : ''}`}>
                        <td className="px-3 py-2 text-center"><input type="checkbox" checked={selectedIds.includes(u._id)} onChange={() => toggleSelect(u._id)} className="rounded" /></td>
                        <td className="px-3 py-2 font-medium">{u.name}</td>
                        <td className="px-3 py-2 text-gray-500">{u.email}</td>
                        <td className="px-3 py-2"><span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">{u.department}</span></td>
                        <td className="px-3 py-2 font-mono text-xs font-bold text-indigo-600">{u.loginCode}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {(result.details?.skipped?.length > 0 || result.details?.errors?.length > 0) && (
            <button onClick={() => setShowDetails(!showDetails)} className="w-full flex items-center justify-center gap-1 text-sm text-gray-500 hover:text-gray-700 py-1 mt-2">
              {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {showDetails ? 'Ocultar detalles' : 'Ver omitidos/errores'}
            </button>
          )}
          {showDetails && (
            <div className="mt-2 space-y-2 text-sm max-h-48 overflow-y-auto">
              {result.details?.skipped?.map((s) => (
                <div key={`${s.email}-${s.name}`} className="bg-yellow-50 border border-yellow-200 rounded p-2 flex justify-between">
                  <span className="font-medium">{s.name}</span><span className="text-yellow-700">{s.reason}</span>
                </div>
              ))}
              {result.details?.errors?.map((e) => (
                <div key={`${e.row}-${e.reason}`} className="bg-red-50 border border-red-200 rounded p-2 flex justify-between">
                  <span className="font-medium">{e.row}</span><span className="text-red-700">{e.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {result?.error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" /><span className="text-sm">{result.error}</span>
        </div>
      )}
    </div>
  );
};

// ===================================================================================
// 4. COMPONENTE: GESTIÓN DE USUARIOS
// ===================================================================================
const UserManagementPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', department: '', email: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/admin/users');
      setUsers(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleSendToAll = async () => {
    if (!window.confirm(`¿Enviar el código de acceso a todos los usuarios activos (${users.filter(u => u.active !== false).length})?`)) return;
    setSendingAll(true);
    try {
      const data = await apiFetch('/api/admin/users/send-codes', {
        method: 'POST',
        body: { userIds: [] },
      });
      alert(`✅ Enviados: ${data.summary.sent} | Fallidos: ${data.summary.failed}`);
    } catch (err) { alert(err.message || 'Error de conexión'); }
    setSendingAll(false);
  };

  const handleEditSave = async () => {
    setSavingEdit(true);
    try {
      await apiFetch(`/api/admin/users/${editingUser._id}`, {
        method: 'PUT',
        body: editForm,
      });
      alert('✅ Usuario actualizado');
      setEditingUser(null);
      await loadUsers();
    } catch (err) { alert(err.message || 'Error de conexión'); }
    setSavingEdit(false);
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.active === false ? true : false;
    const action = newStatus ? 'activar' : 'desactivar';
    if (!window.confirm(`¿${action.charAt(0).toUpperCase() + action.slice(1)} a ${user.name}?`)) return;
    try {
      await apiFetch(`/api/admin/users/${user._id}/status`, {
        method: 'PATCH',
        body: { active: newStatus },
      });
      await loadUsers();
    } catch (err) { alert(err.message || 'Error de conexión'); }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`¿Eliminar a ${user.name}? \n\nEsta acción le quitará el acceso al juego. Por cumplimiento de la LOPD, se conservará un registro legal en la base de datos de esta eliminación.`)) return;
    try {
      await apiFetch(`/api/admin/users/${user._id}`, { method: 'DELETE' });
      await loadUsers();
    } catch (err) { alert(err.message || 'Error de conexión'); }
  };

  const handleResendCode = async (user) => {
    try {
      const data = await apiFetch(`/api/admin/users/${user._id}/resend-code`, { method: 'POST' });
      alert(`✅ ${data.message}`);
    } catch (err) { alert(err.message || 'Error de conexión'); }
  };

  const departments = [...new Set(users.map(u => u.department).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es'));
  const filtered = users.filter(u => {
    const matchSearch = !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    const matchDept = !filterDept || u.department === filterDept;
    const matchStatus = filterStatus === 'all' || (filterStatus === 'active' ? u.active !== false : u.active === false);
    return matchSearch && matchDept && matchStatus;
  });
  const activeCount = users.filter(u => u.active !== false).length;
  const inactiveCount = users.filter(u => u.active === false).length;

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <Users className="w-6 h-6 text-indigo-600" /> Gestión de Usuarios ({users.length})
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={handleSendToAll} disabled={sendingAll || users.length === 0} className="flex items-center gap-1 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg px-3 py-1.5 transition disabled:opacity-50">
            <Mail className={`w-4 h-4 ${sendingAll ? 'animate-spin' : ''}`} />
            {sendingAll ? 'Enviando...' : `Enviar código a todos (${users.filter(u => u.active !== false).length})`}
          </button>
          <button onClick={loadUsers} disabled={loading} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 border rounded-lg px-3 py-1.5 hover:bg-gray-50 transition">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-blue-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-blue-600">{users.length}</p><p className="text-xs text-blue-700">Total</p></div>
        <div className="bg-green-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-green-600">{activeCount}</p><p className="text-xs text-green-700">Activos</p></div>
        <div className="bg-red-50 rounded-lg p-3 text-center"><p className="text-2xl font-bold text-red-600">{inactiveCount}</p><p className="text-xs text-red-700">Inactivos</p></div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input type="text" placeholder="Buscar por nombre o email..." value={search} onChange={e => setSearch(e.target.value)} className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
        <select value={filterDept} onChange={e => setFilterDept(e.target.value)} className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
          <option value="">Todos los dptos.</option>
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
          <option value="all">Todos</option>
          <option value="active">Activos</option>
          <option value="inactive">Inactivos</option>
        </select>
      </div>
      {loading ? (
        <div className="text-center py-8 text-gray-400"><RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />Cargando usuarios...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-400">No se encontraron usuarios con los filtros aplicados.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Departamento</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alta</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filtered.map(user => (
                <tr key={user._id} className={`hover:bg-gray-50 transition ${user.active === false ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${user.active === false ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                      {user.active === false ? <UserX className="w-3 h-3" /> : <UserCheck className="w-3 h-3" />}
                      {user.active === false ? 'Inactivo' : 'Activo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">{user.name}</td>
                  <td className="px-4 py-3 text-gray-500">{user.email}</td>
                  <td className="px-4 py-3"><span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">{user.department}</span></td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{user.createdAt ? new Date(user.createdAt).toLocaleDateString('es-ES') : '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => { setEditingUser(user); setEditForm({ name: user.name, department: user.department, email: user.email }); }} title="Editar usuario" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition"><Save className="w-4 h-4" /></button>
                      <button onClick={() => handleToggleStatus(user)} title={user.active === false ? 'Activar usuario' : 'Desactivar usuario'} className={`p-1.5 rounded-lg transition ${user.active === false ? 'text-green-600 hover:bg-green-50' : 'text-yellow-600 hover:bg-yellow-50'}`}>
                        {user.active === false ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleResendCode(user)} title="Reenviar código por email" className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition"><Mail className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(user)} title="Eliminar usuario" className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-400 mt-2 text-right">Mostrando {filtered.length} de {users.length} usuarios</p>
        </div>
      )}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-indigo-600 p-4 flex justify-between items-center text-white">
              <h3 className="font-bold text-lg">Editar Usuario</h3>
              <button onClick={() => setEditingUser(null)} className="hover:bg-indigo-700 p-1 rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-1">Nombre completo</label>
                <input id="edit-name" type="text" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              <div>
                <label htmlFor="edit-department" className="block text-sm font-medium text-gray-700 mb-1">Departamento</label>
                <input id="edit-department" type="text" value={editForm.department} onChange={e => setEditForm({ ...editForm, department: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              <div>
                <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input id="edit-email" type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              <button onClick={handleEditSave} disabled={savingEdit} className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 transition disabled:opacity-50 flex items-center justify-center gap-2">
                <Save className="w-5 h-5" />{savingEdit ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================================================================================
// 5. COMPONENTE: CORRECCIÓN MODAL
// ===================================================================================
const CorrectionModal = ({ response, onClose, onSaveSuccess }) => {
  const [compromiso, setCompromiso] = useState(0);
  const [satisfaccion, setSatisfaccion] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (response) {
      const analysis = response.correctedAnalysis || response.analysis || {};
      setCompromiso(analysis.compromiso || 0);
      setSatisfaccion(analysis.satisfaccion || 0);
    }
  }, [response]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await apiFetch(`/api/responses/${response._id}/correct`, {
        method: 'PUT',
        body: { compromiso: Number.parseInt(compromiso), satisfaccion: Number.parseInt(satisfaccion) },
      });
      onSaveSuccess();
    } catch (error) { alert('Error al guardar la corrección'); }
    setLoading(false);
  };

  if (!response) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-blue-600 p-4 flex justify-between items-center text-white">
          <h3 className="font-bold text-lg">Corregir Análisis IA</h3>
          <button onClick={onClose} className="hover:bg-blue-700 p-1 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="bg-gray-50 p-3 rounded border">
            <p className="text-xs text-gray-500 mb-1">Respuesta:</p>
            <p className="text-gray-800 italic">"{response.text}"</p>
          </div>
          <div>
            <label htmlFor="compromiso-range" className="block text-sm font-medium text-gray-700 mb-2">Compromiso (1-10)</label>
            <div className="flex items-center gap-4">
              <input id="compromiso-range" type="range" min="1" max="10" value={compromiso} onChange={e => setCompromiso(e.target.value)} className="w-full" />
              <span className="font-bold text-blue-600 w-8 text-center">{compromiso}</span>
            </div>
          </div>
          <div>
            <label htmlFor="satisfaccion-range" className="block text-sm font-medium text-gray-700 mb-2">Satisfacción (1-10)</label>
            <div className="flex items-center gap-4">
              <input id="satisfaccion-range" type="range" min="1" max="10" value={satisfaccion} onChange={e => setSatisfaccion(e.target.value)} className="w-full" />
              <span className="font-bold text-purple-600 w-8 text-center">{satisfaccion}</span>
            </div>
          </div>
          <button onClick={handleSave} disabled={loading} className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition flex items-center justify-center gap-2 disabled:opacity-50">
            <Save className="w-5 h-5" /> {loading ? 'Guardando...' : 'Guardar Corrección'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===================================================================================
// 6. COMPONENTE: CONFIGURACIÓN DE NOTIFICACIONES
// ===================================================================================
const NotificationConfigView = () => {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch('/api/notifications/config');
        setConfig(data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch('/api/notifications/config', {
        method: 'PUT',
        body: config,
      });
      alert('Configuración guardada con éxito');
    } catch { alert('Error al guardar'); }
    setSaving(false);
  };

  if (!config) return <div className="text-center p-4 text-gray-500">Cargando configuración...</div>;
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <h3 className="text-xl font-bold text-gray-800 mb-4">⚙️ Configuración de Notificaciones</h3>
      <div className="space-y-6">
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <span className="font-medium">Activar Notificaciones</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={config.enabled} onChange={e => setConfig({...config, enabled: e.target.checked})} className="sr-only peer" />
            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
<div className="border-t pt-4">
  <h4 className="font-bold text-gray-700 mb-3">📅 Notificación Nueva Semana</h4>
  <div className="grid grid-cols-3 gap-4">
    <div>
      <label htmlFor="newweek-day" className="block text-sm font-medium text-gray-700 mb-1">Día</label>
      <select id="newweek-day" value={config.dayOfWeek} onChange={e => setConfig({...config, dayOfWeek: Number.parseInt(e.target.value)})} className="w-full px-3 py-2 border rounded-md">
        {days.map((d, i) => <option key={i} value={i}>{d}</option>)}
      </select>
    </div>
    <div>
      <label htmlFor="newweek-hour" className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
      <input id="newweek-hour" type="number" min="0" max="23" value={config.hour} onChange={e => setConfig({...config, hour: Number.parseInt(e.target.value)})} className="w-full px-3 py-2 border rounded-md" />
    </div>
    <div>
      <label htmlFor="newweek-minute" className="block text-sm font-medium text-gray-700 mb-1">Minutos</label>
      <input id="newweek-minute" type="number" min="0" max="59" value={config.minute} onChange={e => setConfig({...config, minute: Number.parseInt(e.target.value)})} className="w-full px-3 py-2 border rounded-md" />
    </div>
  </div>
</div>

<div className="border-t pt-4">
  <h4 className="font-bold text-gray-700 mb-3">🔔 Recordatorio Semanal</h4>
  <div className="grid grid-cols-3 gap-4 mb-3">
    <div>
      <label htmlFor="reminder-day" className="block text-sm font-medium text-gray-700 mb-1">Día</label>
      <select id="reminder-day" value={config.reminderDayOfWeek} onChange={e => setConfig({...config, reminderDayOfWeek: Number.parseInt(e.target.value)})} className="w-full px-3 py-2 border rounded-md" disabled={!config.notifyReminder}>
        {days.map((d, i) => <option key={i} value={i}>{d}</option>)}
      </select>
    </div>
    <div>
      <label htmlFor="reminder-hour" className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
      <input id="reminder-hour" type="number" min="0" max="23" value={config.reminderHour} onChange={e => setConfig({...config, reminderHour: Number.parseInt(e.target.value)})} className="w-full px-3 py-2 border rounded-md" disabled={!config.notifyReminder} />
    </div>
    <div>
      <label htmlFor="reminder-minute" className="block text-sm font-medium text-gray-700 mb-1">Minutos</label>
      <input id="reminder-minute" type="number" min="0" max="59" value={config.reminderMinute} onChange={e => setConfig({...config, reminderMinute: Number.parseInt(e.target.value)})} className="w-full px-3 py-2 border rounded-md" disabled={!config.notifyReminder} />
    </div>
  </div>
  <textarea value={config.reminderMessage || ''} onChange={e => setConfig({...config, reminderMessage: e.target.value})} rows="2" disabled={!config.notifyReminder} className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Mensaje del recordatorio..." />
</div>
        <button onClick={handleSave} disabled={saving} className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2">
          <Save className="w-5 h-5" /> {saving ? 'Guardando...' : 'Guardar Configuración'}
        </button>
      </div>
    </div>
  );
};

// ===================================================================================
// 7. COMPONENTE: RANKING DE DEPARTAMENTOS
// ===================================================================================
const DepartmentRankingView = ({ currentWeek, responses }) => {
  const [departmentStats, setDepartmentStats] = useState([]);

  useEffect(() => {
  const calc = async () => {
    try {
      const deptGroups = responses.reduce((acc, r) => {
        const dept = r.department || 'Sin Departamento';
        if (!acc[dept]) acc[dept] = { name: dept, responses: [], totalC: 0, totalS: 0, count: 0, weeks: new Set() };
        const c = r.correctedAnalysis?.compromiso || r.analysis?.compromiso || 0;
        const s = r.correctedAnalysis?.satisfaccion || r.analysis?.satisfaccion || 0;
        acc[dept].responses.push(r);
        acc[dept].totalC += c; acc[dept].totalS += s; acc[dept].count++;
        acc[dept].weeks.add(r.week);
        return acc;
      }, {});

      const stats = await Promise.all(
        Object.values(deptGroups).map(d => computeDeptStats(d, currentWeek))
      );

      stats.sort((a, b) => b.responseCount - a.responseCount);
      setDepartmentStats(stats);
    } catch (e) { console.error(e); }
  };
  if (responses.length > 0) calc();
}, [currentWeek, responses]);

  const medal = (i) => ['🥇','🥈','🥉'][i] || '🔹';
  const colorC = (v) => {
    if (v >= 8) return 'text-green-600';
    if (v >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <h3 className="text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
        <Trophy className="w-6 h-6 text-yellow-500" /> Ranking Global de Departamentos
      </h3>
      <p className="text-sm text-gray-500 mb-4">Estadísticas acumuladas (Semanas 1–{currentWeek})</p>
      {departmentStats.length === 0 ? (
        <p className="text-center py-8 text-gray-400">No hay datos disponibles.</p>
      ) : (
        <div className="space-y-4">
          {departmentStats.map((dept, i) => {
  const sinDatosSuficientes = dept.responseCount < 3; // mismo umbral que backend
  return (
    <div key={dept.name} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{medal(i)}</span>
          <div>
            <h4 className="font-bold text-lg">{dept.name}</h4>
            <p className="text-sm text-gray-500">{dept.responseCount} respuestas · {dept.totalUsers} usuarios · {dept.usersThisWeek} respondieron sem. {currentWeek}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-blue-600">{dept.participationRate}%</div>
          <div className="text-xs text-gray-500">Participación</div>
        </div>
      </div>
      {sinDatosSuficientes ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 mb-3">
          ⚠️ Datos insuficientes para mostrar puntuaciones agregadas (mínimo 3 respuestas por confidencialidad).
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 mb-3">
          <div className="bg-purple-50 rounded-lg p-3">
            <div className="text-xs text-gray-600 mb-1">Compromiso</div>
            <div className={`text-2xl font-bold ${colorC(dept.avgCompromiso)}`}>{dept.avgCompromiso}/10</div>
          </div>
          <div className="bg-pink-50 rounded-lg p-3">
            <div className="text-xs text-gray-600 mb-1">Satisfacción</div>
            <div className={`text-2xl font-bold ${colorC(dept.avgSatisfaccion)}`}>{dept.avgSatisfaccion}/10</div>
          </div>
        </div>
      )}
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div className={`h-2 rounded-full ${dept.participationRate === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(100, dept.participationRate)}%` }} />
      </div>
    </div>
  );
})}
        </div>
      )}
    </div>
  );
};

// ===================================================================================
// 8. COMPONENTE: PANEL DE USO
// ===================================================================================
const UsagePanel = ({ responses, stats, currentWeek, allDepartments }) => {
  const [selectedWeek, setSelectedWeek] = useState(currentWeek);

  useEffect(() => { setSelectedWeek(currentWeek); }, [currentWeek]);

  const totalUsers  = stats?.totalUsers  || 0;
  const activeUsers = stats?.activeUsers  ?? totalUsers;
  const totalResp   = stats?.totalResponses || 0;
  const corrected   = stats?.correctedResponses || 0;

  const firstWeekByUser = {};
  responses.forEach(r => {
    const uid = String(r.userId?._id ?? r.userId ?? '');
    if (!uid) return;
    if (firstWeekByUser[uid] === undefined || r.week < firstWeekByUser[uid]) {
      firstWeekByUser[uid] = r.week;
    }
  });

  const weekOptions = Array.from({ length: currentWeek }, (_, i) => i + 1);
  const weekResponses = responses.filter(r => r.week === selectedWeek);

  const deptMap = {};
  weekResponses.forEach(r => {
    const d = r.department || 'Sin dpto.';
    if (!deptMap[d]) deptMap[d] = { count: 0, newCount: 0 };
    deptMap[d].count++;
    const uid = String(r.userId?._id ?? r.userId ?? '');
    if (uid && firstWeekByUser[uid] === selectedWeek) deptMap[d].newCount++;
  });

  const knownDeptNames = (allDepartments || []).map(d => d.name);
  knownDeptNames.forEach(name => {
    if (!deptMap[name]) deptMap[name] = { count: 0, newCount: 0 };
  });

  const deptList = Object.entries(deptMap).sort((a, b) => b[1].count - a[1].count);
  const maxDeptCount = Math.max(1, ...deptList.map(([, d]) => d.count));

  const uniqueUsersThisWeek = new Set(weekResponses.map(r => String(r.userId?._id ?? r.userId)).filter(Boolean)).size;
  const pctThisWeek = activeUsers > 0 ? Math.min(Math.round((uniqueUsersThisWeek / activeUsers) * 100), 100) : 0;
  const newUsersThisWeek = weekResponses.filter(r => {
    const uid = String(r.userId?._id ?? r.userId ?? '');
    return uid && firstWeekByUser[uid] === selectedWeek;
  }).length;

  const badgeClass = (pct) => {
    if (pct >= 80) return 'bg-green-100 text-green-700';
    if (pct >= 40) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };
  const barColors = ['#534AB7','#1D9E75','#D85A30','#D4537E','#378ADD','#639922','#BA7517'];

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border-l-4 border-cyan-500">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-cyan-600" /> Panel de Uso
        </h3>
        <div className="flex items-center gap-2">
          <label htmlFor="usage-week-select" className="text-sm text-gray-500">Semana:</label>
          <select
            id="usage-week-select"
            value={selectedWeek}
            onChange={e => setSelectedWeek(Number.parseInt(e.target.value))}
            className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 outline-none bg-white"
          >
            {weekOptions.map(w => <option key={w} value={w}>Semana {w}{w === currentWeek ? ' (actual)' : ''}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        {[
          { label: 'Usuarios totales',  value: totalUsers },
          { label: 'Usuarios activos',  value: activeUsers },
          { label: 'Respuestas totales', value: totalResp },
          { label: `Respondieron sem. ${selectedWeek}`, value: `${uniqueUsersThisWeek} (${pctThisWeek}%)` },
          { label: `Altas nuevas sem. ${selectedWeek}`, value: newUsersThisWeek },
          { label: 'Análisis corregidos', value: corrected },
        ].map((k) => (
          <div key={k.label} className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500 mb-1">{k.label}</p>
            <p className="text-2xl font-bold text-gray-800">{k.value}</p>
          </div>
        ))}
      </div>

      <h4 className="text-sm font-semibold text-gray-600 mb-3">Por departamento — Semana {selectedWeek}</h4>
      {deptList.length === 0 ? (
        <p className="text-center py-6 text-gray-400 text-sm">No hay departamentos registrados.</p>
      ) : (
        <div className="space-y-3">
          {deptList.map(([name, d], i) => {
            const w = d.count > 0 ? Math.round((d.count / maxDeptCount) * 100) : 0;
            const pct = activeUsers > 0 ? Math.min(Math.round((d.count / maxDeptCount) * 100), 100) : 0;
            const isZero = d.count === 0;
            return (
              <div key={name} className="flex items-center gap-3">
                <span className={`text-sm min-w-[120px] truncate ${isZero ? 'text-gray-400' : 'text-gray-700'}`} title={name}>{name}</span>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-2 rounded-full transition-all" style={{ width: `${w}%`, background: isZero ? '#e5e7eb' : barColors[i % barColors.length] }} />
                </div>
                <span className="text-xs text-gray-500 min-w-[30px] text-right">{d.count} resp.</span>
                {d.newCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700" title="Altas nuevas (debut)">
                    ✓ {d.newCount} nueva{d.newCount > 1 ? 's' : ''}
                  </span>
                )}
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full min-w-[44px] text-center ${isZero ? 'bg-gray-100 text-gray-400' : badgeClass(pct)}`}>{pct}%</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ===================================================================================
// 9. LOGIN VIEW

const LoginView = ({ loginUser, requestAdminLogin, verifyAdminTotp, loading }) => {
  const [loginCode, setLoginCode] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [recoveryStep, setRecoveryStep] = useState(1);
  const [reqLoading, setReqLoading] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminStep, setAdminStep] = useState('credentials');
  const [totpToken, setTotpToken] = useState('');
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  const handleAdminCredentials = async (e) => {
    e.preventDefault();
    if (!adminEmail.trim() || !adminPassword.trim()) return;
    const ok = await requestAdminLogin(adminEmail, adminPassword);
    setAdminPassword('');
    if (ok) setAdminStep('totp');
  };

  const handleAdminTotp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(totpToken)) return;
    await verifyAdminTotp(totpToken);
    setTotpToken('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!acceptedPrivacy) return;
    if (loginCode.trim()) {
      await loginUser(loginCode, acceptedPrivacy);
      setLoginCode('');
      setShowCode(false);
    }
  };

  const handleSSO = () => {
    if (!acceptedPrivacy) return;
    sessionStorage.setItem('sso_consent', 'true');
    window.location.href = `${API_BASE_URL}/api/auth/azure/login`;
  };

  const handleRecoverRequest = async (e) => {
    e.preventDefault();
    setReqLoading(true);
    try {
      await apiFetch('/api/users/recover-request', { method: 'POST', body: { email } });
      setRecoveryStep(2);
    } catch (err) { alert(err.message || 'Error'); }
    setReqLoading(false);
  };

  const handleVerifyAndRecover = async (e) => {
    e.preventDefault();
    setReqLoading(true);
    try {
      await apiFetch('/api/users/verify-recovery', { method: 'POST', body: { email, code: verificationCode } });
      alert('¡Identidad verificada! Revisa tu correo.');
      setVerificationCode('');
      setMode('login');
      setRecoveryStep(1);
    } catch (err) { alert(err.message || 'Código incorrecto'); }
    setReqLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full bg-white p-10 rounded-2xl shadow-xl">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3"><BrandLogo size="lg" /></div>
          <h2 className="text-3xl font-extrabold text-gray-900">
            {mode === 'recover' ? 'Recuperar Código' : mode === 'admin' ? 'Administración' : 'Bienvenido'}
          </h2>
          {mode === 'login' && (
            <div className="mt-3 flex justify-center gap-4 text-sm">
              <span className="text-blue-600 font-bold underline">Entrar</span>
              <span className="text-gray-300">|</span>
              <button onClick={() => setMode('recover')} className="text-gray-500 hover:text-blue-500">¿Olvidaste tu código?</button>
            </div>
          )}
        </div>

        {/* ------------------------- LOGIN CON CÓDIGO ------------------------- */}
        {mode === 'login' && (
          <form onSubmit={handleLogin} autoComplete="off" className="space-y-6">
            <div>
              <label htmlFor="login-code" className="block text-sm font-bold text-gray-700 mb-2 text-center">Código de Acceso</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Key className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="login-code"
                  name="access-code"
                  type={showCode ? 'text' : 'password'}
                  autoComplete="one-time-code"
                  autoCorrect="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                  required
                  className="pl-10 pr-12 block w-full px-3 py-4 border-2 border-gray-300 rounded-lg text-center text-2xl tracking-widest font-mono uppercase"
                  placeholder="XXXXXX"
                  value={loginCode}
                  onChange={e => setLoginCode(e.target.value.toUpperCase())}
                  maxLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowCode(s => !s)}
                  aria-label={showCode ? 'Ocultar código' : 'Mostrar código'}
                  title={showCode ? 'Ocultar código' : 'Mostrar código'}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showCode ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <div className="text-right mt-2">
                <button type="button" onClick={() => setMode('admin')} className="text-xs text-gray-400 hover:text-purple-500 flex items-center gap-1 ml-auto">
                  <Shield className="w-3 h-3" /> Panel de Control
                </button>
              </div>
            </div>

            <div className="flex items-start gap-2 bg-blue-50 p-3 rounded-lg border border-blue-100">
              <input
                type="checkbox"
                id="privacy"
                checked={acceptedPrivacy}
                onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                className="mt-1 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
              />
              <label htmlFor="privacy" className="text-xs text-gray-600 leading-tight cursor-pointer">
                He leído y acepto la <a href="/politica-privacidad.pdf" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-medium">Política de Privacidad</a>. Comprendo que mis respuestas libres serán analizadas de forma anónima por Inteligencia Artificial para generar métricas departamentales.
              </label>
            </div>

            <button type="submit" disabled={loading || !acceptedPrivacy} className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-700 transition disabled:opacity-50">
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
            <button
              type="button"
              onClick={handleSSO}
              disabled={!acceptedPrivacy}
              className="w-full py-3 border-2 border-gray-300 rounded-xl font-bold text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
            >
              Entrar con Microsoft
            </button>
          </form>
        )}

        {/* ------------------------- ADMIN: CREDENCIALES ------------------------- */}
        {mode === 'admin' && adminStep === 'credentials' && (
          <form onSubmit={handleAdminCredentials} className="space-y-4">
            <div className="bg-purple-50 p-3 rounded-lg text-xs text-purple-800 text-center border border-purple-200">
              Acceso restringido a administradores
            </div>
            <input
              type="email"
              required
              autoComplete="username"
              className="block w-full px-3 py-3 border rounded-lg"
              placeholder="tu@empresa.com"
              value={adminEmail}
              onChange={e => setAdminEmail(e.target.value)}
            />
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="password"
                required
                autoComplete="current-password"
                className="pl-10 block w-full px-3 py-3 border rounded-lg"
                placeholder="••••••••"
                value={adminPassword}
                onChange={e => setAdminPassword(e.target.value)}
              />
            </div>
            <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white py-3 rounded-xl font-bold hover:bg-purple-700 transition">
              Continuar
            </button>
            <button type="button" onClick={() => setMode('login')} className="w-full text-xs text-gray-500 hover:underline">Volver</button>
          </form>
        )}

        {/* ------------------------- ADMIN: TOTP ------------------------- */}
        {mode === 'admin' && adminStep === 'totp' && (
          <form onSubmit={handleAdminTotp} autoComplete="off" className="space-y-4">
            <div className="bg-purple-50 p-3 rounded-lg text-xs text-purple-800 text-center border border-purple-200">
              Introduce el código de 6 dígitos de tu app de autenticación
            </div>
            <input
              type="text"
              name="totp-code"
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              data-lpignore="true"
              data-1p-ignore="true"
              maxLength={6}
              className="block w-full px-3 py-4 border-2 border-purple-300 rounded-lg text-center text-3xl tracking-[0.75rem] font-bold"
              placeholder="000000"
              value={totpToken}
              onChange={e => setTotpToken(e.target.value.replace(/\D/g, ''))}
            />
            <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white py-3 rounded-xl font-bold hover:bg-purple-700 transition">
              {loading ? 'Verificando...' : 'Verificar y entrar'}
            </button>
            <button type="button" onClick={() => { setAdminStep('credentials'); setTotpToken(''); }} className="w-full text-xs text-gray-500 hover:underline">
              ← Volver
            </button>
          </form>
        )}

        {/* ------------------------- RECUPERAR CÓDIGO ------------------------- */}
        {mode === 'recover' && (
          <div className="space-y-4">
            {recoveryStep === 1 ? (
              <form onSubmit={handleRecoverRequest} className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 text-sm text-blue-800">Introduce tu correo para recibir un código de verificación.</div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Mail className="h-5 w-5 text-gray-400" /></div>
                  <input type="email" required autoComplete="email" className="pl-10 block w-full px-3 py-3 border rounded-lg" placeholder="tu@correo.com" value={email} onChange={e => setEmail(e.target.value)} />
                </div>
                <button type="submit" disabled={reqLoading} className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition">{reqLoading ? 'Enviando...' : 'Enviar Código'}</button>
              </form>
            ) : (
              <form onSubmit={handleVerifyAndRecover} autoComplete="off" className="space-y-4">
                <div className="bg-green-50 p-3 rounded-lg border border-green-200 text-sm text-green-800">Introduce el código de 6 dígitos que te hemos enviado.</div>
                <input
                  type="text"
                  name="verification-code"
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  className="block w-full px-3 py-4 border-2 border-blue-300 rounded-lg text-center text-3xl tracking-[1rem] font-bold"
                  placeholder="000000"
                  maxLength={6}
                  value={verificationCode}
                  onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                />
                <button type="submit" disabled={reqLoading} className="w-full bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 transition">{reqLoading ? 'Verificando...' : 'Verificar'}</button>
                <button type="button" onClick={() => setRecoveryStep(1)} className="w-full text-sm text-gray-500 hover:underline">Volver</button>
              </form>
            )}
            <button type="button" onClick={() => { setMode('login'); setRecoveryStep(1); }} className="w-full text-sm text-gray-500 hover:underline">← Volver al inicio</button>
          </div>
        )}
      </div>
    </div>
  );
};



// ===================================================================================
// 10. GAME VIEW
// ===================================================================================
const GameView = ({ currentUser, currentWeek, questions, submitResponse, ranking, weeklyProgress, departmentResponses, userStars, userTriangles, loading, logoutUser }) => {  const [responseText, setResponseText] = useState('');
  const [hasResponded, setHasResponded] = useState(false);
  const currentQuestion = questions.find(q => q.week === currentWeek);
  const [showMyResponses, setShowMyResponses] = useState(false);
  useEffect(() => {
    const check = async () => {
      if (!currentUser?._id) return;
      try {
        const d = await apiFetch(`/api/responses/check/${currentUser._id}/${currentWeek}`);
        setHasResponded(d.hasResponded);
      } catch (e) { console.error(e); }
    };
    check();
  }, [currentWeek, currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (responseText.trim() && currentQuestion?._id) {
      await submitResponse(currentQuestion._id, responseText);
      setResponseText('');
      setHasResponded(true);
    }
  };

  const userRankPos = ranking.findIndex(item => item._id === currentUser.department) + 1;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 flex justify-between items-center">
          <div>
            <div className="mb-2"><BrandLogo /></div>
            <h2 className="text-3xl font-bold text-gray-800">¡Hola, {currentUser.name}!</h2>
            <p className="text-gray-600">Departamento: {currentUser.department}</p>
            
          </div>
<div className="flex flex-col items-end gap-2">
  <div className="flex items-center gap-2"><Calendar className="w-6 h-6 text-blue-600" /><span className="text-sm text-gray-500">Semana {currentWeek}</span></div>
  <button onClick={() => setShowMyResponses(true)} className="text-xs text-gray-400 hover:text-blue-500">Mis respuestas</button>
  <button onClick={logoutUser} className="text-xs text-gray-400 hover:text-red-500">Cerrar sesión</button>
</div>
{showMyResponses && <MyResponsesPanel onClose={() => setShowMyResponses(false)} />}    
</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" /> Ranking</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl font-bold text-yellow-500">#{userRankPos || '-'}</span>
                <div>
                  <span className="font-medium text-lg">{currentUser.department}</span>
                  <div className="mt-1">{renderRankingIcons(userTriangles, userStars)}</div>
                </div>
              </div>
              <span className="text-3xl font-bold text-gray-600">{departmentResponses}</span>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-green-600" /> Progreso esta Semana</h3>
            <p className="text-gray-600 font-medium mb-2">{currentUser.department}</p>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
              <div className={`h-2.5 rounded-full ${weeklyProgress === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${Math.max(0, Math.min(100, weeklyProgress))}%` }} />
            </div>
            <div className="flex justify-between text-sm text-gray-700"><span>Progreso</span><span className="font-bold">{weeklyProgress}%</span></div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-blue-600 mb-4">Pregunta de la Semana {currentWeek}</h3>
          {currentQuestion ? (
            <>
              <p className="text-lg font-medium text-gray-800 mb-6 border-l-4 border-blue-500 pl-4 py-2 bg-blue-50">{currentQuestion.text}</p>
              {hasResponded ? (
                <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4">
                  <p className="font-bold">¡Gracias por participar!</p>
                  <p>Ya has enviado tu respuesta. Vuelve la próxima semana.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <textarea value={responseText} onChange={e => setResponseText(e.target.value)} placeholder="Escribe tu respuesta aquí..." required rows="5" className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                  <button type="submit" disabled={loading} className="w-full flex justify-center py-3 px-4 rounded-md shadow-sm text-lg font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
                    {loading ? 'Enviando...' : <><Send className="w-5 h-5 mr-2" /> Enviar Respuesta</>}
                  </button>
                </form>
              )}
            </>
          ) : (
            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
              <p className="font-bold">Aún no hay pregunta</p>
              <p>El administrador publicará la pregunta pronto.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MyResponsesPanel = ({ onClose }) => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch('/api/users/me/responses');
        setResponses(data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <div className="bg-blue-600 p-4 flex justify-between items-center text-white flex-shrink-0">
          <h3 className="font-bold text-lg">Mis respuestas</h3>
          <button onClick={onClose} className="hover:bg-blue-700 p-1 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 overflow-y-auto space-y-4">
          {loading ? (
            <p className="text-center text-gray-400">Cargando...</p>
          ) : responses.length === 0 ? (
            <p className="text-center text-gray-400">Todavía no has respondido a ninguna pregunta.</p>
          ) : (
            responses.map((r) => (
                <div key={r.week} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-bold text-blue-600">Semana {r.week}</span>
                  <span className="text-xs text-gray-400">
                    {r.submittedAt ? new Date(r.submittedAt).toLocaleDateString('es-ES') : ''}
                  </span>
                </div>
                <p className="text-gray-700 italic mb-3">"{r.text}"</p>
                <div className="flex gap-4 text-sm">
                  <span>Compromiso: <strong>{r.compromiso ?? '-'}</strong>/10</span>
                  <span>Satisfacción: <strong>{r.satisfaccion ?? '-'}</strong>/10</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ===================================================================================
// COMPONENTE: CREAR ADMINISTRADOR (solo superadmin)
// ===================================================================================
const CreateAdminPanel = () => {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (form.password.length < 12) {
      alert('La contraseña debe tener al menos 12 caracteres.');
      return;
    }
    setCreating(true);
    setResult(null);
    try {
      const data = await apiFetch('/api/admin/create', {
        method: 'POST',
        body: form,
      });
      setResult(data);
      setForm({ name: '', email: '', password: '', role: 'admin' });
    } catch (err) {
      setResult({ error: err.message });
    }
    setCreating(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border-l-4 border-purple-500">
      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
        <Shield className="w-6 h-6 text-purple-600" /> Crear Administrador
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Solo disponible para superadministradores. Comparte el QR resultante únicamente con la persona destinataria, por un canal seguro.
      </p>
      <form onSubmit={handleCreate} className="space-y-3">
        <input
          type="text" required placeholder="Nombre completo"
          value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
        />
        <input
          type="email" required placeholder="Email"
          value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
        />
        <input
          type="password" required placeholder="Contraseña (mín. 12 caracteres)"
          value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
        />
        <select
          value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg bg-white"
        >
          <option value="admin">Admin</option>
          <option value="superadmin">Superadmin</option>
        </select>
        <button type="submit" disabled={creating} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition disabled:opacity-50">
          {creating ? 'Creando...' : 'Crear Administrador'}
        </button>
      </form>

      {result?.error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {result.error}
        </div>
      )}

      {result?.qrDataUrl && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <p className="text-sm font-medium text-green-800 mb-3">
            ✅ Administrador creado: {result.email}
          </p>
          <img src={result.qrDataUrl} alt="Código QR TOTP" className="mx-auto mb-3 rounded" style={{ maxWidth: '200px' }} />
          <p className="text-xs text-gray-600">Escanéalo con Google Authenticator, Authy, etc.</p>
          <p className="text-xs text-gray-500 mt-2 font-mono break-all">
            Clave manual: {result.manualSecret}
          </p>
          <p className="text-xs text-red-600 mt-2 font-medium">
            ⚠️ Este código no volverá a mostrarse. Compártelo ahora por un canal seguro.
          </p>
        </div>
      )}
    </div>
  );
};
// ===================================================================================
// 11. COMPONENTE: BANCO DE PREGUNTAS (PDF)
// ===================================================================================
const QuestionBankPanel = ({ currentWeek, onQuestionCreated }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loadingQ, setLoadingQ] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const fileInputRef = useRef(null);

  const loadQuestions = useCallback(async () => {
    setLoadingQ(true);
    try {
      const data = await apiFetch('/api/admin/question-bank');
      setQuestions(data);
    } catch (e) { console.error(e); }
    setLoadingQ(false);
  }, []);

  useEffect(() => { loadQuestions(); }, [loadQuestions]);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const data = await apiFetch('/api/admin/upload-question-bank', {
        method: 'POST',
        body: formData,
      });
      setUploadResult(data);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadQuestions();
    } catch (e) { setUploadResult({ error: e.message }); }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta pregunta del banco?')) return;
    try {
      await apiFetch(`/api/admin/question-bank/${id}`, { method: 'DELETE' });
      await loadQuestions();
    } catch (err) { alert(err.message || 'Error de conexión'); }
  };

  const handleSelectForWeek = async () => {
    const unused = questions.filter(q => !q.used);
    if (unused.length === 0) return alert('No quedan preguntas sin usar en el banco.');
    if (!window.confirm(`Se seleccionará automáticamente una pregunta aleatoria del banco para la Semana ${currentWeek}.\n\nQuedan ${unused.length} preguntas disponibles. ¿Continuar?`)) return;
    setSelecting(true);
    try {
      const data = await apiFetch('/api/admin/question-bank/select-for-week', {
        method: 'POST',
        body: { week: currentWeek },
      });
      alert(`✅ Pregunta asignada a Semana ${currentWeek}:\n\n"${data.question.text}"\n\nQuedan ${data.remaining} preguntas sin usar.`);
      await loadQuestions();
      onQuestionCreated();
    } catch (err) { alert(err.message || 'Error de conexión'); }
    setSelecting(false);
  };

  const unused = questions.filter(q => !q.used);
  const used = questions.filter(q => q.used);

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border-l-4 border-teal-500">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2"><span style={{ fontSize: '22px' }}>📄</span>Banco de Preguntas (PDF)</h3>
        <div className="flex items-center gap-2">
          <button onClick={loadQuestions} disabled={loadingQ} title="Actualizar lista" className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition">
            <RefreshCw className={`w-4 h-4 ${loadingQ ? 'animate-spin' : ''}`} />
          </button>
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{unused.length} disponibles · {used.length} usadas</span>
        </div>
      </div>
      <div
  onClick={() => fileInputRef.current?.click()}
  onKeyDown={(e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  }}
  role="button"
  tabIndex={0}
  aria-label="Seleccionar archivo PDF"
  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition mb-3 ${file ? 'border-teal-400 bg-teal-50' : 'border-gray-300 bg-gray-50 hover:border-teal-400 hover:bg-teal-50'}`}
>
        <input ref={fileInputRef} type="file" accept=".pdf" onChange={e => { if (e.target.files[0]) { setFile(e.target.files[0]); setUploadResult(null); } }} className="hidden" />
        {file ? (
          <div><CheckCircle className="w-8 h-8 text-teal-500 mx-auto mb-1" /><p className="font-medium text-teal-700">{file.name}</p><p className="text-xs text-teal-600">{(file.size / 1024).toFixed(1)} KB</p></div>
        ) : (
          <div><Upload className="w-8 h-8 text-gray-400 mx-auto mb-1" /><p className="font-medium text-gray-600">Sube un manual o documento PDF</p><p className="text-xs text-gray-400">La IA extraerá las preguntas automáticamente</p></div>
        )}
      </div>
      <div className="flex gap-2 mb-4">
        <button onClick={handleUpload} disabled={!file || uploading} className="flex-1 bg-teal-600 text-white py-2 rounded-lg font-medium hover:bg-teal-700 transition disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
          {uploading ? <><RefreshCw className="w-4 h-4 animate-spin" /> Procesando...</> : <><Upload className="w-4 h-4" /> Procesar PDF</>}
        </button>
        <button onClick={handleSelectForWeek} disabled={selecting || unused.length === 0} className="flex-1 bg-indigo-600 text-white py-2 rounded-lg font-medium hover:bg-indigo-700 transition disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
          {selecting ? <><RefreshCw className="w-4 h-4 animate-spin" /> Seleccionando...</> : <>🎲 Usar aleatoria en Semana {currentWeek}</>}
        </button>
      </div>
      {uploadResult && !uploadResult.error && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">✅ {uploadResult.summary?.added} preguntas añadidas · {uploadResult.summary?.skipped} ya existían</div>
      )}
      {uploadResult?.error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" /> {uploadResult.error}</div>
      )}
      {loadingQ ? (
        <div className="text-center py-6 text-gray-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-1" />Cargando banco...</div>
      ) : questions.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-sm border rounded-lg bg-gray-50">El banco de preguntas está vacío. Sube un PDF para comenzar.</div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {unused.length > 0 && (
            <>
              <p className="text-xs font-semibold text-teal-700 uppercase tracking-wide px-1 pt-1">Disponibles ({unused.length})</p>
              {unused.map(q => (
                <div key={q._id} className="flex items-start gap-2 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2">
                  <span className="text-xs text-teal-600 mt-0.5">●</span>
                  <p className="flex-1 text-sm text-gray-700 leading-snug">{q.text}</p>
                  <button onClick={() => handleDelete(q._id)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition flex-shrink-0" title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </>
          )}
          {used.length > 0 && (
            <>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1 pt-2">Ya usadas ({used.length})</p>
              {used.map(q => (
                <div key={q._id} className="flex items-start gap-2 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 opacity-60">
                  <span className="text-xs text-gray-400 mt-0.5">✓</span>
                  <p className="flex-1 text-sm text-gray-500 leading-snug line-through">{q.text}</p>
                  <span className="text-xs text-gray-400 flex-shrink-0 mt-0.5">Sem. {q.usedInWeek}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ColorField = ({ label, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={HEX_RE.test(value) ? value : '#000000'}
        onChange={e => onChange(e.target.value)}
        className="h-10 w-14 p-1 border rounded cursor-pointer bg-white"
      />
      <input
        type="text"
        value={value}
        maxLength={7}
        onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) onChange(e.target.value); }}
        className="w-28 px-3 py-2 border rounded-lg font-mono text-sm"
      />
    </div>
  </div>
);

const ThemeCustomizationPanel = () => {
  const { theme, setTheme, reloadTheme } = useTheme();
  const [draft, setDraft] = useState(theme);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const dirtyRef = useRef(false);
  const logoInputRef = useRef(null);

  // Si se cierra el panel con cambios sin guardar, se revierte la vista previa
  useEffect(() => () => { if (dirtyRef.current) reloadTheme(); }, [reloadTheme]);

  const colorsValid = HEX_RE.test(draft.primaryColor) && HEX_RE.test(draft.secondaryColor) && HEX_RE.test(draft.backgroundColor);

  const update = (patch) => {
    dirtyRef.current = true;
    const next = { ...draft, ...patch };
    setDraft(next);
    setMessage(null);
    if (HEX_RE.test(next.primaryColor) && HEX_RE.test(next.secondaryColor) && HEX_RE.test(next.backgroundColor)) {
      setTheme(next); // vista previa en vivo
    }
  };

  const handleSave = async () => {
    if (!colorsValid || !draft.appName.trim()) return;
    setSaving(true);
    try {
      const saved = await apiFetch('/api/theme', {
        method: 'PUT',
        body: {
          appName: draft.appName.trim(),
          primaryColor: draft.primaryColor,
          secondaryColor: draft.secondaryColor,
          backgroundColor: draft.backgroundColor,
          fontFamily: draft.fontFamily,
          borderRadius: draft.borderRadius,
        },
      });
      dirtyRef.current = false;
      setDraft(saved);
      setTheme(saved);
      setMessage({ type: 'ok', text: 'Personalización guardada.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Error al guardar' });
    }
    setSaving(false);
  };

  const handleResetDefaults = () => {
    const { logoData, ...defaults } = DEFAULT_THEME;
    update(defaults); // se aplica en vivo; hay que pulsar Guardar para confirmar
  };

  const handleLogoChange = async (e) => {
    const f = e.target.files[0];
    e.target.value = '';
    if (!f) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(f.type)) {
      return setMessage({ type: 'error', text: 'Formato no permitido. Usa PNG, JPG o WebP.' });
    }
    if (f.size > 300 * 1024) {
      return setMessage({ type: 'error', text: 'El logo no puede superar 300 KB.' });
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', f);
      const { logoData } = await apiFetch('/api/admin/theme/logo', { method: 'POST', body: fd });
      setDraft(d => ({ ...d, logoData }));
      setTheme(t => ({ ...t, logoData }));
      setMessage({ type: 'ok', text: 'Logo actualizado.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Error al subir el logo' });
    }
    setUploading(false);
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('¿Quitar el logo?')) return;
    try {
      await apiFetch('/api/admin/theme/logo', { method: 'DELETE' });
      setDraft(d => ({ ...d, logoData: null }));
      setTheme(t => ({ ...t, logoData: null }));
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Error al quitar el logo' });
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border-l-4 border-pink-500">
      <h3 className="text-xl font-bold text-gray-800 mb-1 flex items-center gap-2">
        <Palette className="w-6 h-6 text-pink-600" /> Personalización
      </h3>
      <p className="text-sm text-gray-500 mb-5">Los cambios se previsualizan al instante, pero no se aplican a los demás usuarios hasta que pulses Guardar.</p>

      <div className="space-y-5">
        <div>
          <label htmlFor="theme-appname" className="block text-sm font-medium text-gray-700 mb-1">Nombre de la aplicación</label>
          <input id="theme-appname" type="text" maxLength={60} value={draft.appName} onChange={e => update({ appName: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
        </div>

        <div>
          <p className="block text-sm font-medium text-gray-700 mb-2">Logo</p>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="h-20 w-40 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
              {draft.logoData
                ? <img src={draft.logoData} alt="Logo actual" className="max-h-16 max-w-[9rem] object-contain" />
                : <span className="text-xs text-gray-400">Sin logo</span>}
            </div>
            <div className="flex flex-col gap-2">
              <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoChange} className="hidden" />
              <button type="button" onClick={() => logoInputRef.current?.click()} disabled={uploading} className="text-sm bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 disabled:opacity-50 flex items-center gap-2">
                <Upload className="w-4 h-4" /> {uploading ? 'Subiendo...' : 'Subir logo'}
              </button>
              {draft.logoData && (
                <button type="button" onClick={handleRemoveLogo} className="text-sm text-red-600 border border-red-200 px-4 py-2 rounded-lg hover:bg-red-50 flex items-center gap-2">
                  <Trash2 className="w-4 h-4" /> Quitar logo
                </button>
              )}
              <p className="text-xs text-gray-400">PNG, JPG o WebP · máx. 300 KB · mejor con fondo transparente</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <ColorField label="Color principal" value={draft.primaryColor} onChange={v => update({ primaryColor: v })} />
          <ColorField label="Color secundario (admin)" value={draft.secondaryColor} onChange={v => update({ secondaryColor: v })} />
          <ColorField label="Color de fondo" value={draft.backgroundColor} onChange={v => update({ backgroundColor: v })} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="theme-font" className="block text-sm font-medium text-gray-700 mb-1">Tipografía</label>
            <select id="theme-font" value={draft.fontFamily} onChange={e => update({ fontFamily: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-white">
              {Object.entries(FONT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="theme-radius" className="block text-sm font-medium text-gray-700 mb-1">Esquinas</label>
            <select id="theme-radius" value={draft.borderRadius} onChange={e => update({ borderRadius: e.target.value })} className="w-full px-3 py-2 border rounded-lg bg-white">
              {Object.entries(RADIUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </div>

        {message && (
          <div className={`rounded-lg p-3 text-sm border ${message.type === 'ok' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
            {message.text}
          </div>
        )}

        <div className="flex gap-3">
          <button type="button" onClick={handleResetDefaults} className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition">
            Restaurar valores por defecto
          </button>
          <button type="button" onClick={handleSave} disabled={saving || !colorsValid || !draft.appName.trim()} className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition disabled:opacity-50 flex items-center justify-center gap-2">
            <Save className="w-5 h-5" /> {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};
// ===================================================================================
// 12. ADMIN VIEW
// ===================================================================================
const AdminView = ({ questions, currentWeek, responses, stats, createQuestion, updateQuestion, generateAIQuestion, advanceWeek, loadAdminData, logoutAdmin, loading }) => {
  const [questionText, setQuestionText] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [showNotificationConfig, setShowNotificationConfig] = useState(false);
  const [showDeptConfig, setShowDeptConfig] = useState(false);
  const [showUserImport, setShowUserImport] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showQuestionBank, setShowQuestionBank] = useState(false);
  const [showUsagePanel, setShowUsagePanel] = useState(false);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [showTheme, setShowTheme] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deptList, setDeptList] = useState([]);
  const [newDeptName, setNewDeptName] = useState('');
  const currentQuestion = questions.find(q => q.week === currentWeek);

  useEffect(() => { if (currentQuestion) setQuestionText(currentQuestion.text); else setQuestionText(''); }, [currentQuestion, currentWeek]);
  useEffect(() => { loadDepartments(); }, []);

  const loadDepartments = async () => {
    try { const data = await apiFetch('/api/departments'); setDeptList(data); } catch (e) { console.error(e); }
  };

  const handleAddDepartment = async () => {
    if (!newDeptName.trim()) return;
    try {
      await apiFetch('/api/departments', { method: 'POST', body: { name: newDeptName } });
      setNewDeptName('');
      loadDepartments();
    } catch (err) { alert(err.message || 'Error'); }
  };

  const handleDeleteDepartment = async (id) => {
    if (!window.confirm('¿Borrar este departamento?')) return;
    try {
      await apiFetch(`/api/departments/${id}`, { method: 'DELETE' });
      loadDepartments();
    } catch { alert('Error'); }
  };

  const handleSaveQuestion = async () => {
    if (!questionText.trim()) return alert('La pregunta no puede estar vacía.');
    if (currentQuestion) await updateQuestion(currentQuestion._id, questionText);
    else { await createQuestion(questionText); setQuestionText(''); }
  };

  const handleGenerateAI = async () => {
    setGeneratingAI(true);
    await generateAIQuestion();
    setGeneratingAI(false);
  };

  const handleAdvanceWeek = async () => {
    await advanceWeek();
  };

  const handleChangeWeek = async () => {
    const newWeek = prompt(`Número de semana (actual: ${currentWeek}):`);
    if (!newWeek || Number.isNaN(Number.parseInt(newWeek))) return;
    try {
      await apiFetch('/api/config/week', { method: 'PUT', body: { currentWeek: Number.parseInt(newWeek) } });
      alert(`Semana cambiada a ${newWeek}.`);
      await loadAdminData();
    } catch (err) { alert(err.message || 'Error'); }
  };

  const generateReportHTML = (report) => {
  const fecha = new Date(report.fechaGeneracion).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const deptHTML = report.departamentos.sort((a, b) => b.totalRespuestas - a.totalRespuestas).map(dept => {
    const sinDatosSuficientes = dept.promedioCompromiso === null;

    return `
      <div style="background:white;border-radius:12px;padding:24px;margin-bottom:24px;box-shadow:0 2px 8px rgba(0,0,0,.1)">
        <h2 style="color:#1f2937;margin:0 0 20px;font-size:24px;border-bottom:3px solid #3b82f6;padding-bottom:12px">🏢 ${escapeHTML(dept.departamento)}</h2>
        ${sinDatosSuficientes ? `
          <div style="background:#fef9c3;border-left:4px solid #eab308;padding:16px;border-radius:8px">
            <strong>⚠️ Datos insuficientes por confidencialidad</strong>
            <p style="margin:8px 0 0;color:#713f12">${escapeHTML(dept.analisis)}</p>
          </div>
        ` : `
          <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:16px;margin-bottom:24px">
            <div style="background:linear-gradient(135deg,#667eea,#764ba2);border-radius:8px;padding:16px;color:white"><div style="font-size:14px;opacity:.9">Compromiso</div><div style="font-size:32px;font-weight:bold">${dept.promedioCompromiso}/10</div></div>
            <div style="background:linear-gradient(135deg,#f093fb,#f5576c);border-radius:8px;padding:16px;color:white"><div style="font-size:14px;opacity:.9">Satisfacción</div><div style="font-size:32px;font-weight:bold">${dept.promedioSatisfaccion}/10</div></div>
            <div style="background:linear-gradient(135deg,#4facfe,#00f2fe);border-radius:8px;padding:16px;color:white"><div style="font-size:14px;opacity:.9">Respuestas</div><div style="font-size:32px;font-weight:bold">${dept.totalRespuestas}</div></div>
          </div>
          ${dept.alertas?.length ? `<div style="background:#fef2f2;border-left:4px solid #ef4444;padding:16px;margin-bottom:20px"><strong style="color:#991b1b">⚠️ Alertas</strong><ul style="color:#7f1d1d">${dept.alertas.map(a => `<li>${escapeHTML(a)}</li>`).join('')}</ul></div>` : ''}
          <h3 style="color:#374151;font-size:18px">📊 Análisis</h3>
          <p style="color:#4b5563;line-height:1.6">${escapeHTML(dept.analisis)}</p>
          <h3 style="color:#374151;font-size:18px;margin-top:16px">💡 Recomendaciones</h3>
          <ul style="color:#4b5563;line-height:1.8">${(dept.recomendaciones || []).map(r => `<li>${escapeHTML(r)}</li>`).join('')}</ul>
        `}
      </div>`;
  }).join('');

  return `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Informe</title><style>body{font-family:sans-serif;background:linear-gradient(135deg,#667eea,#764ba2);padding:40px 20px}.container{max-width:1200px;margin:0 auto;background:#f9fafb;border-radius:16px;overflow:hidden}.header{background:linear-gradient(135deg,#667eea,#764ba2);color:white;padding:40px;text-align:center}.content{padding:40px}</style></head><body><div class="container"><div class="header"><h1>📊 Informe de Departamentos</h1><p>${fecha}</p></div><div class="content"><div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:20px;margin-bottom:40px">${report.departamentos.length > 0 ? `<div style="background:linear-gradient(135deg,#f093fb,#f5576c);border-radius:12px;padding:24px;color:white;text-align:center"><h3>Departamentos</h3><p style="font-size:36px;font-weight:bold">${report.totalDepartamentos}</p></div><div style="background:linear-gradient(135deg,#4facfe,#00f2fe);border-radius:12px;padding:24px;color:white;text-align:center"><h3>Total Respuestas</h3><p style="font-size:36px;font-weight:bold">${report.totalRespuestas}</p></div>` : ''}</div>${deptHTML}</div></div></body></html>`;
};

  const handleDownloadReport = async () => {
    setGeneratingReport(true);
    try {
      const report = await apiFetch('/api/admin/generate-report', { method: 'POST' });
      const blob = new Blob([generateReportHTML(report)], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = `informe_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
      alert('✅ Informe descargado');
    } catch (e) { alert(e.message); }
    setGeneratingReport(false);
  };

  const groupedResponses = responses.reduce((acc, r) => { const w = r.week || 'Sin Semana'; if (!acc[w]) acc[w] = []; acc[w].push(r); return acc; }, {});
  const sortedWeeks = Object.keys(groupedResponses).sort((a, b) => Number.parseInt(a) - Number.parseInt(b));

  const SectionToggle = ({ label, icon, color, active, onToggle }) => (
    <button onClick={onToggle} className={`w-full py-3 rounded-lg font-medium transition flex items-center justify-center gap-2 text-white ${color}`}>
      {icon} {active ? `Ocultar ${label}` : label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 flex justify-between items-center">
            <div className="flex items-center gap-3"><BrandLogo /><h2 className="text-3xl font-bold text-purple-600">Panel de Administración</h2></div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2"><Calendar className="w-6 h-6 text-purple-600" /><span className="text-sm text-gray-500">Semana {currentWeek}</span></div>
            <button onClick={logoutAdmin} className="text-xs text-gray-400 hover:text-red-500">Cerrar sesión</button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl shadow p-3">
            <SectionToggle label="Importar Usuarios" icon={<Upload className="w-4 h-4" />} color="bg-blue-600 hover:bg-blue-700" active={showUserImport} onToggle={() => setShowUserImport(!showUserImport)} />
          </div>
          <div className="bg-white rounded-xl shadow p-3">
            <SectionToggle label="Gestionar Usuarios" icon={<Users className="w-4 h-4" />} color="bg-indigo-600 hover:bg-indigo-700" active={showUserManagement} onToggle={() => setShowUserManagement(!showUserManagement)} />
          </div>
          <div className="bg-white rounded-xl shadow p-3">
            <SectionToggle label="Notificaciones" icon={<span>🔔</span>} color="bg-purple-600 hover:bg-purple-700" active={showNotificationConfig} onToggle={() => setShowNotificationConfig(!showNotificationConfig)} />
          </div>
          <div className="bg-white rounded-xl shadow p-3">
            <SectionToggle label="Departamentos" icon={<Building className="w-4 h-4" />} color="bg-orange-500 hover:bg-orange-600" active={showDeptConfig} onToggle={() => setShowDeptConfig(!showDeptConfig)} />
          </div>
          <div className="bg-white rounded-xl shadow p-3">
            <SectionToggle label="Banco PDF" icon={<span style={{fontSize:'16px'}}>📄</span>} color="bg-teal-600 hover:bg-teal-700" active={showQuestionBank} onToggle={() => setShowQuestionBank(!showQuestionBank)} />
          </div>
          <div className="bg-white rounded-xl shadow p-3">
            <SectionToggle label="Panel de Uso" icon={<BarChart2 className="w-4 h-4" />} color="bg-cyan-600 hover:bg-cyan-700" active={showUsagePanel} onToggle={() => setShowUsagePanel(!showUsagePanel)} />
          </div>
          <div className="bg-white rounded-xl shadow p-3">
           <SectionToggle label="Crear Admin" icon={<Shield className="w-4 h-4" />} color="bg-purple-800 hover:bg-purple-900" active={showCreateAdmin} onToggle={() => setShowCreateAdmin(!showCreateAdmin)} />
         </div>
         <div className="bg-white rounded-xl shadow p-3">
  <SectionToggle label="Personalización" icon={<Palette className="w-4 h-4" />} color="bg-pink-600 hover:bg-pink-700" active={showTheme} onToggle={() => setShowTheme(!showTheme)} />
</div>
        </div>

        <div className="mb-6">
          <button onClick={handleDownloadReport} disabled={generatingReport} className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-xl font-medium hover:from-green-700 hover:to-emerald-700 transition disabled:opacity-50 flex items-center justify-center gap-2">
            <TrendingUp className="w-5 h-5" /> {generatingReport ? 'Generando Informe...' : 'Descargar Informe con IA'}
          </button>
        </div>

        {showUserImport && <UserImportPanel onImportDone={() => loadAdminData()} />}
        {showUserManagement && <UserManagementPanel />}
        {showQuestionBank && <QuestionBankPanel currentWeek={currentWeek} onQuestionCreated={() => loadAdminData()} />}
        {showNotificationConfig && <NotificationConfigView />}
        {showCreateAdmin && <CreateAdminPanel />}
        {showTheme && <ThemeCustomizationPanel />}

        {showUsagePanel && (
          <UsagePanel
            responses={responses}
            stats={stats}
            currentWeek={currentWeek}
          />
        )}

        {showDeptConfig && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Gestión de Departamentos</h3>
            <div className="flex gap-2 mb-4">
              <input type="text" placeholder="Nuevo Departamento" className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" value={newDeptName} onChange={e => setNewDeptName(e.target.value)} />
              <button onClick={handleAddDepartment} className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"><Plus className="w-5 h-5" /> Añadir</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {deptList.map(dept => (
                <div key={dept._id} className="flex justify-between items-center bg-gray-50 p-3 rounded-lg border">
                  <span className="font-medium">{dept.name}</span>
                  <button onClick={() => handleDeleteDepartment(dept._id)} className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50"><Trash2 className="w-5 h-5" /></button>
                </div>
              ))}
              {deptList.length === 0 && <p className="text-gray-500 italic col-span-3 text-center py-4">No hay departamentos.</p>}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Gestión del Juego — Semana {currentWeek}</h3>
          <div className="space-y-4">
            <textarea value={questionText} onChange={e => setQuestionText(e.target.value)} placeholder="Pregunta para la semana actual..." className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none min-h-[100px]" />
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={handleSaveQuestion} disabled={loading} className={`flex-1 text-white py-3 rounded-lg font-medium transition disabled:opacity-50 ${currentQuestion ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                {loading ? 'Procesando...' : (currentQuestion ? '💾 Guardar Cambios' : '➕ Crear Pregunta')}
              </button>
              <button onClick={handleGenerateAI} disabled={generatingAI || loading} className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {generatingAI ? 'Generando...' : <><Plus className="w-5 h-5" /> Generar con IA</>}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 border-t pt-4">
              <button onClick={handleAdvanceWeek} disabled={loading} className="bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50">➡️ Avanzar a Semana {currentWeek + 1}</button>
              <button onClick={handleChangeWeek} disabled={loading} className="bg-orange-600 text-white py-3 rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50">🔢 Cambiar Semana</button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { icon: <Users className="w-8 h-8 text-blue-500" />, label: 'Usuarios', value: stats?.totalUsers || 0 },
            { icon: <UserCheck className="w-8 h-8 text-green-500" />, label: 'Activos', value: stats?.activeUsers || 0 },
            { icon: <Send className="w-8 h-8 text-orange-500" />, label: 'Respuestas', value: stats?.totalResponses || 0 },
            { icon: <Trophy className="w-8 h-8 text-yellow-500" />, label: 'Semana', value: currentWeek },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl shadow-lg p-6 flex flex-col items-center">
              {s.icon}
              <p className="text-sm text-gray-500 mt-2">{s.label}</p>
              <p className="text-3xl font-bold text-gray-800">{s.value}</p>
            </div>
          ))}
        </div>

        <DepartmentRankingView currentWeek={currentWeek} responses={responses} />

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Respuestas ({responses.length})</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Sem.', 'Usuario', 'Respuesta', 'Comp.', 'Satis.', 'Acción'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedWeeks.length > 0 ? sortedWeeks.map(week => (
                  <React.Fragment key={week}>
                    <tr className="bg-blue-50">
                      <td colSpan="6" className="px-4 py-2 text-sm font-bold text-blue-800">📂 SEMANA {week}</td>
                    </tr>
                    {groupedResponses[week].map(r => (
                      <tr key={r._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-500">{r.week}</td>
                        <td className="px-4 py-3 font-mono text-gray-400 text-xs">
  <Lock className="w-3 h-3 inline mr-1" />{r.userPseudonym}
</td>
                        <td className="px-4 py-3 text-gray-500 max-w-xs truncate">{r.text}</td>
                        <td className="px-4 py-3 text-gray-500">{r.correctedAnalysis?.compromiso || r.analysis?.compromiso || '-'}</td>
                        <td className="px-4 py-3 text-gray-500">{r.correctedAnalysis?.satisfaccion || r.analysis?.satisfaccion || '-'}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => { setSelectedResponse(r); setIsModalOpen(true); }} className="text-indigo-600 hover:text-indigo-900 text-xs font-medium">Corregir</button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                )) : (
                  <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400">No hay respuestas.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {isModalOpen && (
          <CorrectionModal
            response={selectedResponse}
            onClose={() => { setIsModalOpen(false); setSelectedResponse(null); }}
            onSaveSuccess={() => { setIsModalOpen(false); setSelectedResponse(null); loadAdminData(); }}
          />
        )}
      </div>
    </div>
  );
};

// ===================================================================================
// 13. COMPONENTE PRINCIPAL APP
// ===================================================================================
function App() {
  const [view, setView] = useState('login');
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(0);
  const [stats, setStats] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [weeklyProgress, setWeeklyProgress] = useState(0);
  const [departmentResponses, setDepartmentResponses] = useState(0);
  const [userStars, setUserStars] = useState(0);
  const [userTriangles, setUserTriangles] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consentGiven, setConsentGiven] = useState(false);
const [theme, setTheme] = useState(DEFAULT_THEME);

const reloadTheme = useCallback(async () => {
  try {
    const t = await apiFetch('/api/theme');
    setTheme({ ...DEFAULT_THEME, ...t });
  } catch (e) { console.error(e); }
}, []);

useEffect(() => { reloadTheme(); }, [reloadTheme]);
useEffect(() => { document.title = theme.appName; }, [theme.appName]);

useEffect(() => {
    if (!currentUser && !isAdmin) return;
    const interval = setInterval(async () => {
      try {
        if (currentUser) {
          const config = await apiFetch('/api/config');
          if (config.currentWeek !== currentWeek) {
            setCurrentWeek(config.currentWeek);
            alert(`¡La semana ha avanzado a la Semana ${config.currentWeek}!`);
            await loadQuestions();
          }
        }
      } catch (e) { console.error(e); }
    }, 30000);
    return () => clearInterval(interval);
  }, [currentUser, isAdmin, currentWeek]);

  useEffect(() => {
    const base = API_BASE_URL || 'http://localhost:5000';
    const wsUrl = base.replace(/^http/, 'ws') + '/ws';

    let socket;
    try {
      socket = new WebSocket(wsUrl);
    } catch (err) {
      console.error('No se pudo crear la conexión WebSocket:', err, wsUrl);
      return;
    }

    socket.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'WEEK_ADVANCED') {
          setCurrentWeek(msg.data.currentWeek);
          alert(`¡Hemos avanzado a la Semana ${msg.data.currentWeek}!`);
          loadQuestions();
          if (currentUser?._id) loadGameStats(currentUser._id, msg.data.currentWeek);
        } else if (['NEW_RESPONSE', 'RESPONSE_CORRECTED'].includes(msg.type) && currentUser) {
          loadGameStats(currentUser._id, currentWeek);
        }
      } catch (e) { console.error(e); }
    };
    socket.onerror = (e) => console.error('WS error:', e);
    return () => { if (socket.readyState === WebSocket.OPEN) socket.close(); };
  }, [currentUser, currentWeek, isAdmin]);



  useEffect(() => {
    (async () => {
      setInitialLoading(true);
      try {
        const config = await apiFetch('/api/config');
        setCurrentWeek(config.currentWeek);
      } catch (err) {
        setError(`${err.message}. Verifica que el backend esté corriendo en ${API_BASE_URL}`);
      }
      setInitialLoading(false);
    })();
  }, []);


const requestAdminLogin = async (email, password) => {
  setLoading(true);
  try {
    await apiFetch('/api/admin/login', { method: 'POST', body: { email, password } });
    return true; // el LoginView pasará al paso de TOTP
  } catch (err) {
    alert(err.message || 'Credenciales incorrectas');
    return false;
  } finally {
    setLoading(false);
  }
};

const verifyAdminTotp = async (token) => {
  setLoading(true);
  try {
    await apiFetch('/api/admin/login/verify-totp', { method: 'POST', body: { token } });
    await loadAdminData();
    return true;
  } catch (err) {
    alert(err.message || 'Código incorrecto');
    return false;
  } finally {
    setLoading(false);
  }
};


  const loadAdminData = async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/admin/dashboard', { method: 'POST' });
      setQuestions(data.questions);
      setResponses(data.responses);
      setStats(data.stats);
      setCurrentWeek(data.config.currentWeek);
      setIsAdmin(true);
      setView('admin');
    } catch (err) {
      alert(err.message || 'Sesión de administrador inválida');
      setIsAdmin(false);
    }
    setLoading(false);
  };

  const logoutAdmin = async () => {
    try {
      await apiFetch('/api/admin/logout', { method: 'POST' });
    } catch (e) { console.error(e); }
    setIsAdmin(false);
    setView('login');
  };

const loginUser = async (loginCode, consent) => {
  setConsentGiven(consent);
  setLoading(true);
  try {
    const user = await apiFetch('/api/users/login', { method: 'POST', body: { loginCode } });
    setCurrentUser(user);
    await Promise.all([loadQuestions(), loadGameStats(user._id, currentWeek)]);
    setView('game');
  } catch (err) { alert(err.message || 'Código inválido'); }
  setLoading(false);
};

const logoutUser = async () => {
  try {
    await apiFetch('/api/users/logout', { method: 'POST' });
  } catch (e) { console.error(e); }
  setCurrentUser(null);
  setView('login');
};

const submitResponse = async (questionId, responseText) => {
  if (typeof responseText !== 'string') return;
  setLoading(true);
  try {
    await apiFetch('/api/responses', {
      method: 'POST',
      body: { questionId, userId: currentUser._id, department: currentUser.department, text: responseText, week: currentWeek, consent: consentGiven },
    });
    await loadGameStats(currentUser._id, currentWeek);
  } catch (err) { alert(err.message); }
  setLoading(false);
};

  const createQuestion = async (text) => {
    setLoading(true);
    try {
      await apiFetch('/api/questions', { method: 'POST', body: { text, week: currentWeek || 1 } });
      alert('✅ Pregunta creada.');
      await loadAdminData();
    } catch (err) { alert(err.message); }
    setLoading(false);
  };

  const updateQuestion = async (id, text) => {
    setLoading(true);
    try {
      await apiFetch(`/api/questions/${id}`, { method: 'PUT', body: { text } });
      alert('✅ Pregunta actualizada.');
      await loadAdminData();
    } catch (err) { alert(err.message); }
    setLoading(false);
  };

  const generateAIQuestion = async () => {
    setLoading(true);
    try {
      await apiFetch('/api/questions/ai', { method: 'POST', body: { currentWeek } });
      alert('✅ Pregunta generada.');
      await loadAdminData();
    } catch (err) { alert(err.message || 'Error generando con IA'); }
    setLoading(false);
  };

  const advanceWeek = async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/config/week', { method: 'PUT', body: { currentWeek: currentWeek + 1 } });
      if (data.autoQuestionAssigned) {
        alert(`Semana ${currentWeek + 1}.\n\n✅ Pregunta asignada automáticamente del banco:\n"${data.autoQuestionText}"`);
      } else {
        alert(`Semana ${currentWeek + 1}.\n\n⚠️ No se asignó pregunta automática (banco vacío o ya existe pregunta para esta semana). Recuerda crear una manualmente.`);
      }
      await loadAdminData();
    } catch (err) { alert(err.message || 'Error avanzando semana'); }
    setLoading(false);
  };

  const loadGameStats = useCallback(async (userId, week) => {
    try {
      const d = await apiFetch(`/api/stats/ranking?week=${week}`);
      setRanking(d.ranking); setWeeklyProgress(d.weeklyProgress);
      setDepartmentResponses(d.departmentResponses);
      setUserStars(d.stars || 0); setUserTriangles(d.triangles || 0);
    } catch (e) { console.error(e); }
  }, []);

  const loadQuestions = async () => {
    try {
      const data = await apiFetch('/api/questions');
      setQuestions(data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (initialLoading) return; // espera a que currentWeek esté cargada
    const params = new URLSearchParams(window.location.search);
    const sso = params.get('sso');
    const ssoError = params.get('sso_error');
    if (!sso && !ssoError) return;
    window.history.replaceState({}, '', window.location.pathname);

    if (ssoError) {
      const msgs = {
        not_registered: 'Tu cuenta de Microsoft no está dada de alta en el juego. Contacta con el administrador.',
        inactive: 'Tu cuenta está desactivada.',
        cancelled: 'Inicio de sesión cancelado.',
      };
      alert(msgs[ssoError] || 'No se pudo iniciar sesión con Microsoft.');
      return;
    }

    (async () => {
      try {
        const user = await apiFetch('/api/users/me');
        setConsentGiven(sessionStorage.getItem('sso_consent') === 'true');
        sessionStorage.removeItem('sso_consent');
        setCurrentUser(user);
        await Promise.all([loadQuestions(), loadGameStats(user._id, currentWeek)]);
        setView('game');
      } catch (e) { alert(e.message || 'Error al recuperar la sesión'); }
    })();
  }, [initialLoading]);

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto" />
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center p-6 bg-white rounded-xl shadow-xl max-w-md">
          <h2 className="text-2xl font-bold text-red-600">Error de Conexión</h2>
          <p className="mt-2 text-gray-600">{error}</p>
          <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">Reintentar</button>
        </div>
      </div>
    );
  }

  return (
  <ThemeContext.Provider value={{ theme, setTheme, reloadTheme }}>
    <ThemeStyles theme={theme} />
    {view === 'login' && <LoginView loginUser={loginUser} requestAdminLogin={requestAdminLogin}  verifyAdminTotp={verifyAdminTotp} loading={loading} />}
    {view === 'game' && (
      <GameView currentUser={currentUser} currentWeek={currentWeek} questions={questions} submitResponse={submitResponse}
        ranking={ranking} weeklyProgress={weeklyProgress} departmentResponses={departmentResponses}
        userStars={userStars} userTriangles={userTriangles} loading={loading} logoutUser={logoutUser} />
    )}
    {view === 'admin' && (
      <AdminView questions={questions} currentWeek={currentWeek} responses={responses} stats={stats}
        createQuestion={createQuestion} updateQuestion={updateQuestion} generateAIQuestion={generateAIQuestion}
        advanceWeek={advanceWeek} loadAdminData={loadAdminData} logoutAdmin={logoutAdmin} loading={loading} />
    )}
  </ThemeContext.Provider>
);

}

export default App;